import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import CarouselPhoto1 from "../../../assets/carousel-photo-1.jpg";
import CarouselPhoto2 from "../../../assets/carousel-photo-2.jpg";
import CarouselPhoto3 from "../../../assets/carousel-photo-3.jpg";
import CarouselPhoto4 from "../../../assets/carousel-photo-4.jpg";
import CarouselPhoto5 from "../../../assets/carousel-photo-5.jpg";
import CarouselPhoto6 from "../../../assets/carousel-photo-6.jpg";
import CarouselPhoto7 from "../../../assets/carousel-photo-7.jpg";
import CarouselPhoto8 from "../../../assets/carousel-photo-8.jpg";
import SlideIn from "../../ui/SlideIn/SlideIn.js";
import "./GalleryStyles.scss";

const GALLERY_PHOTOS = [
	{ src: CarouselPhoto1, alt: "Event 1" },
	{ src: CarouselPhoto2, alt: "Event 2" },
	{ src: CarouselPhoto3, alt: "Event 3" },
	{ src: CarouselPhoto4, alt: "Event 4" },
	{ src: CarouselPhoto5, alt: "Event 5" },
	{ src: CarouselPhoto6, alt: "Event 6" },
	{ src: CarouselPhoto7, alt: "Event 7" },
	{ src: CarouselPhoto8, alt: "Event 8" },
];

function GalleryArrow({ className, onClick, direction }) {
	return (
		<button
			type="button"
			className={`gallery-arrow gallery-arrow--${direction} ${className || ""}`}
			onClick={onClick}
			aria-label={direction === "prev" ? "Previous" : "Next"}
		>
			{direction === "prev" ? "‹" : "›"}
		</button>
	);
}

function Gallery() {
	const { t } = useTranslation();
	const [lightboxIndex, setLightboxIndex] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const suppressClickRef = useRef(false);
	const pointerStartRef = useRef(null);

	const openLightbox = (index) => {
		if (suppressClickRef.current) return;
		setLightboxIndex(index);
	};

	const handlePointerDown = (event) => {
		pointerStartRef.current = {
			x: event.clientX ?? event.touches?.[0]?.clientX ?? 0,
			y: event.clientY ?? event.touches?.[0]?.clientY ?? 0,
		};
		suppressClickRef.current = false;
		setIsDragging(false);
	};

	const handlePointerMove = (event) => {
		if (!pointerStartRef.current) return;
		const x = event.clientX ?? event.touches?.[0]?.clientX ?? 0;
		const y = event.clientY ?? event.touches?.[0]?.clientY ?? 0;
		const dx = Math.abs(x - pointerStartRef.current.x);
		const dy = Math.abs(y - pointerStartRef.current.y);
		if (dx > 8 || dy > 8) {
			suppressClickRef.current = true;
			setIsDragging(true);
		}
	};

	const handlePointerUp = () => {
		pointerStartRef.current = null;
		setIsDragging(false);
		if (suppressClickRef.current) {
			window.setTimeout(() => {
				suppressClickRef.current = false;
			}, 120);
		}
	};

	const settings = {
		dots: true,
		infinite: true,
		speed: 500,
		slidesToShow: 3,
		slidesToScroll: 1,
		centerMode: false,
		autoplay: false,
		arrows: true,
		nextArrow: <GalleryArrow direction="next" />,
		prevArrow: <GalleryArrow direction="prev" />,
		onSwipe: () => {
			suppressClickRef.current = true;
			setIsDragging(true);
		},
		responsive: [
			{
				breakpoint: 1200,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 1,
				},
			},
			{
				breakpoint: 768,
				settings: {
					slidesToShow: 1,
					slidesToScroll: 1,
					centerMode: true,
					centerPadding: "10px",
				},
			},
		],
	};

	useEffect(() => {
		if (lightboxIndex === null) return undefined;

		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		const onKeyDown = (event) => {
			if (event.key === "Escape") setLightboxIndex(null);
			if (event.key === "ArrowRight") {
				setLightboxIndex((index) => (index + 1) % GALLERY_PHOTOS.length);
			}
			if (event.key === "ArrowLeft") {
				setLightboxIndex(
					(index) => (index - 1 + GALLERY_PHOTOS.length) % GALLERY_PHOTOS.length
				);
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => {
			document.body.style.overflow = prevOverflow;
			window.removeEventListener("keydown", onKeyDown);
		};
	}, [lightboxIndex]);

	const lightboxPhoto =
		lightboxIndex !== null ? GALLERY_PHOTOS[lightboxIndex] : null;

	return (
		<section className="gallery">
			<div className="gallery-container">
				<div className="gallery-heading">
					<SlideIn direction="up" delay={100} threshold={0.1}>
						<h1>{t("gallery.title")}</h1>
					</SlideIn>
					<SlideIn direction="up" delay={100} threshold={0.2}>
						<p
							className="gallery-text"
							dangerouslySetInnerHTML={{ __html: t("gallery.text") }}
						/>
					</SlideIn>
					<SlideIn direction="up" delay={180} threshold={0.15}>
						<div className="gallery-line"></div>
					</SlideIn>
				</div>
				<div
					className={`gallery-carousel ${isDragging ? "is-dragging" : ""}`}
					onMouseDown={handlePointerDown}
					onMouseMove={handlePointerMove}
					onMouseUp={handlePointerUp}
					onMouseLeave={handlePointerUp}
					onTouchStart={handlePointerDown}
					onTouchMove={handlePointerMove}
					onTouchEnd={handlePointerUp}
				>
					<Slider {...settings}>
						{GALLERY_PHOTOS.map((photo, index) => (
							<div key={photo.src}>
								<button
									type="button"
									className="gallery-carousel__slide"
									onClick={() => openLightbox(index)}
									aria-label={`Open ${photo.alt}`}
								>
									<img src={photo.src} alt={photo.alt} />
								</button>
							</div>
						))}
					</Slider>
				</div>
			</div>

			{lightboxPhoto &&
				createPortal(
					<div
						className="gallery-lightbox"
						role="dialog"
						aria-modal="true"
						aria-label={lightboxPhoto.alt}
						onClick={() => setLightboxIndex(null)}
					>
						<button
							type="button"
							className="gallery-lightbox__close"
							aria-label="Close"
							onClick={() => setLightboxIndex(null)}
						>
							×
						</button>
						<button
							type="button"
							className="gallery-lightbox__nav gallery-lightbox__nav--prev"
							aria-label="Previous"
							onClick={(event) => {
								event.stopPropagation();
								setLightboxIndex(
									(index) =>
										(index - 1 + GALLERY_PHOTOS.length) % GALLERY_PHOTOS.length
								);
							}}
						>
							‹
						</button>
						<img
							className="gallery-lightbox__image"
							src={lightboxPhoto.src}
							alt={lightboxPhoto.alt}
							onClick={(event) => event.stopPropagation()}
						/>
						<button
							type="button"
							className="gallery-lightbox__nav gallery-lightbox__nav--next"
							aria-label="Next"
							onClick={(event) => {
								event.stopPropagation();
								setLightboxIndex((index) => (index + 1) % GALLERY_PHOTOS.length);
							}}
						>
							›
						</button>
					</div>,
					document.body
				)}
		</section>
	);
}

export default Gallery;
